import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getRelated, getTitleById } from "@/lib/catalog";
import { useFavorites } from "@/lib/favorites";
import Badge, { tagTone } from "@/components/base/Badge";
import TitleGrid from "@/components/feature/TitleGrid";
import EmptyState from "@/components/feature/EmptyState";
import PlayerBox from "@/pages/detail/components/PlayerBox";
import ShareModal from "@/pages/detail/components/ShareModal";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const film = id ? getTitleById(id) : undefined;
  const [shareOpen, setShareOpen] = useState(false);
  const { isFavorite, toggle } = useFavorites();

  usePageMeta(
    film ? `${film.title} - 欧美精品在线观看` : "影片详情 - 欧美精品在线观看",
    film
      ? `${film.title}（${film.originalTitle}）${film.year} 年 ${film.region}${film.type === "movie" ? "电影" : "电视剧"}，导演 ${film.director}，主演 ${film.cast.join("、")}。${film.synopsis}`
      : "影片详情页",
    film ? `/detail/${film.id}` : "/detail"
  );

  if (!film) {
    return (
      <div className="mx-auto w-full max-w-[1000px] px-4 py-16 md:px-8">
        <EmptyState
          title="影片不存在或已下线"
          description="你访问的影片信息暂时无法找到，可以返回首页查看其它精选内容。"
        />
      </div>
    );
  }

  const related = getRelated(film, 6);
  const favorited = isFavorite(film.id);

  const metaRows = [
    { label: "导演", value: film.director },
    { label: "主演", value: film.cast.join(" / ") },
    { label: "类型", value: film.genres.join(" / ") },
    { label: "地区", value: film.region },
    { label: "语言", value: film.language },
    { label: "上映", value: `${film.year} 年` },
    { label: "片长", value: film.duration },
    ...(film.episodes ? [{ label: "集数", value: film.episodes }] : []),
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-xs text-foreground-400">
        <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link
          to={`/category?type=${film.type}`}
          className="transition-colors hover:text-accent-400 cursor-pointer"
        >
          {film.type === "movie" ? "电影" : "电视剧"}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-200">{film.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr] lg:gap-8">
        <div className="mx-auto w-full max-w-[220px] lg:mx-0 lg:max-w-none">
          <div className="overflow-hidden rounded-lg border border-background-800 bg-background-900">
            <img
              src={film.cover}
              alt={`${film.title} ${film.originalTitle} 海报`}
              title={`${film.title} - 欧美影视海报`}
              className="aspect-[3/4] w-full object-cover object-top"
            />
          </div>
          <div className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-background-800 bg-background-900 py-3">
            <i className="ri-star-fill text-lg text-accent-500" />
            <span className="font-heading text-xl font-bold text-foreground-50">
              {film.rating.toFixed(1)}
            </span>
            <span className="text-xs text-foreground-500">/ 10</span>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2 py-0.5 text-[11px] font-medium text-foreground-50">
              {film.type === "movie" ? "电影" : "电视剧"}
            </span>
            {film.tags.map((tag) => (
              <Badge key={tag} label={tag} tone={tagTone(tag)} />
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-4xl">
            {film.title}
            {film.endYear ? (
              <span className="ml-3 align-middle text-base font-normal text-foreground-400">
                {film.year}–{film.endYear}
              </span>
            ) : (
              <span className="ml-3 align-middle text-base font-normal text-foreground-400">
                {film.year}
              </span>
            )}
          </h1>
          <p className="mt-1.5 font-label text-sm tracking-wider text-accent-400">
            {film.originalTitle}
          </p>

          <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
            {metaRows.map((row) => (
              <div key={row.label} className="flex gap-3 text-sm">
                <span className="w-14 shrink-0 text-foreground-500">{row.label}</span>
                <span className="min-w-0 text-foreground-200">{row.value}</span>
              </div>
            ))}
          </div>

          {film.awards.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-400">
              <i className="ri-award-line text-accent-400" />
              {film.awards.map((award) => (
                <span key={award} className="rounded-md bg-background-800 px-2 py-1">
                  {award}
                </span>
              ))}
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => toggle(film.id)}
              aria-pressed={favorited}
              className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                favorited
                  ? "bg-accent-500 text-foreground-950"
                  : "border border-background-700 text-foreground-100 hover:border-accent-500 hover:text-accent-400"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} />
              {favorited ? "已收藏" : "收藏"}
            </button>

            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-100 transition-colors hover:border-primary-500 hover:text-foreground-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-forward-line text-base" />
              分享
            </button>

            <Link
              to={`/category?type=${film.type}`}
              className="inline-flex items-center gap-2 rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-500 hover:text-foreground-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-go-back-line text-base" />
              返回分类
            </Link>
          </div>
        </div>
      </div>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <h2 className="mb-3 font-heading text-lg font-bold text-foreground-50">播放预览</h2>
          <PlayerBox title={film.title} cover={film.cover} />
        </div>

        <div>
          <h2 className="mb-3 font-heading text-lg font-bold text-foreground-50">剧情简介</h2>
          <div className="rounded-lg border border-background-800 bg-background-900 p-5">
            <p className="text-sm leading-relaxed text-foreground-300">{film.synopsis}</p>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-background-800 pt-4">
              {film.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genres=${encodeURIComponent(genre)}`}
                  className="rounded-full bg-secondary-500/15 px-3 py-1 text-xs text-secondary-300 transition-colors hover:bg-secondary-500/25 cursor-pointer"
                >
                  {genre}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-4 font-heading text-lg font-bold text-foreground-50">制作团队</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-4 rounded-lg border border-background-800 bg-background-900 p-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500">
              <i className="ri-user-star-line text-xl text-foreground-50" />
            </span>
            <div>
              <p className="text-xs text-foreground-500">导演 / 主创</p>
              <p className="mt-0.5 text-sm font-medium text-foreground-100">{film.director}</p>
            </div>
          </div>

          <div className="rounded-lg border border-background-800 bg-background-900 p-4">
            <p className="text-xs text-foreground-500">主要演员</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {film.cast.map((actor) => (
                <span
                  key={actor}
                  className="rounded-md bg-background-800 px-2.5 py-1 text-xs text-foreground-200"
                >
                  {actor}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <div className="mb-5 flex items-center gap-3">
          <span className="block h-5 w-1 rounded-full bg-accent-500" />
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-50 md:text-xl">
              相关推荐
            </h2>
            <p className="mt-1 text-xs text-foreground-400">根据类型与标签为你推荐相似作品</p>
          </div>
        </div>
        <TitleGrid items={related} className="lg:grid-cols-3 xl:grid-cols-6" />
      </section>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={film.title} />
    </div>
  );
}